import React, { useMemo, useState } from 'react';
import {
  Banner,
  Button,
  Checkbox,
  FormFeedback,
  RadioCard,
  TextArea
} from '@morressier/alembic';
import { Box, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/pro-light-svg-icons';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  emailContentSchema,
  rateReviewSchema
} from '@publishing-engine/domain/validation';

import { RATING_OPTIONS } from 'src/utils/reviews';
import ProtectedReviewWorkflowAction from 'src/components/ProtectedAction/ProtectedReviewWorkflowAction';
import SubmissionInfoWithAuthors from './SubmissionInfoWithAuthors';
import { Overlay } from '../../Overlay';
import { Layout } from '../../Layout';
import ComposeEmail, { EmailContentForm } from '../../EmailModule';
import { useQuery } from '@tanstack/react-query';
import { getAllTemplatesByCommand } from '../../../api/email-service';
import Divider from '../../Divider';
import { IEmailCommandPayload } from '@publishing-engine/domain/types/email-template';
import { normalizeRecipients } from '../../../utils/normalizeEmailRecipients';
import { scrollToFirstError } from '../../../utils/scrollToFirstError';

interface Props {
  email: string;
  reviewId: string;
  reviewRequestId: string;
  onClose: () => void;
  onRateReview: (
    rating: string,
    justification?: string,
    emails?: IEmailCommandPayload
  ) => void;
  organizationId: string;
  receiveFeedback?: string;
}

const RateReviewWithEmail: React.FC<Props> = ({
  email,
  reviewId,
  reviewRequestId,
  onClose,
  onRateReview,
  organizationId,
  receiveFeedback
}) => {
  const {
    register,
    formState: { errors },
    reset,
    trigger,
    watch
  } = useForm({
    defaultValues: {
      rating: '',
      justification: ''
    },
    resolver: zodResolver(rateReviewSchema)
  });
  const { rating, justification } = watch();

  const [withEmail, setWithEmail] = useState<boolean>(false);

  const resetForm = () => {
    reset({
      rating: '',
      justification: ''
    });
  };

  const emailContentMethods = useForm<EmailContentForm>({
    defaultValues: {
      emailContent: {
        body: '',
        subject: '',
        recipients: { to: [], cc: [], bcc: [] },
        attachments: []
      }
    },
    resolver: zodResolver(emailContentSchema)
  });

  const { getValues } = emailContentMethods;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate react-hook-form fields
    const isFormValid = await trigger(); // validates rating + justification

    // If rating is missing → scroll to error
    if (!isFormValid) {
      scrollToFirstError();
      return;
    }

    // Email validation
    if (withEmail) {
      const isEmailValid = await emailContentMethods.trigger();
      if (!isEmailValid) {
        scrollToFirstError();
        return;
      }
    }

    // Build email payload
    const baseEmailContext = getValues('emailContent');
    const templateRecipientsNormalized = normalizeRecipients(
      baseEmailContext.recipients
    );

    const emails = withEmail
      ? [
          {
            ...baseEmailContext,
            recipients: { ...templateRecipientsNormalized }
          }
        ]
      : [];

    // Submit
    onRateReview(rating, justification, emails);
    resetForm();
  };

  const reviewDetails = useMemo(
    () => ({
      reviewId: reviewRequestId,
      reviewRequestId: reviewId
    }),
    [reviewId, reviewRequestId]
  );

  const { data: emailTemplates } = useQuery({
    queryKey: ['browse-email-templates', organizationId, 'RateReview'],
    queryFn: () =>
      organizationId
        ? getAllTemplatesByCommand({
            organizationId,
            commandName: 'RateReview'
          })
        : null,
    enabled: !!organizationId
  });

  return (
    <Overlay>
      <form onSubmit={onSubmit}>
        <Layout width="1440" paddingY={4}>
          <Grid templateColumns="repeat(12, 1fr)" paddingX={8} gap={4}>
            <GridItem colSpan={12}>
              <SubmissionInfoWithAuthors />
            </GridItem>
            <GridItem colSpan={12}>
              <Flex gap={6} flexDirection="column" alignItems="flex-start">
                <FontAwesomeIcon icon={faStar} fontSize="32px" />
                {/* Header */}
                <Box>
                  <Text textStyle="headingGroup">Rate This Review</Text>
                  <Text>
                    Use the following rating to provide a score for this review.
                    You can also add a rating justification if needed.
                  </Text>
                </Box>

                {/* Radio card */}
                <Flex flexDirection="row" gap={2} width="100%" flexWrap="wrap">
                  {Object.keys(RATING_OPTIONS).map(option => {
                    const { label, value } = RATING_OPTIONS[option] || {};

                    return (
                      <RadioCard
                        key={option}
                        ariaLabel={`${label}-rating`}
                        title={value}
                        body={label}
                        value={option}
                        register={register('rating')}
                        checked={watch('rating') === option}
                        width="158px"
                        height="100px"
                      />
                    );
                  })}
                </Flex>
                {errors.rating && (
                  <FormFeedback variant="error">
                    {errors.rating.message}
                  </FormFeedback>
                )}

                {/* Justification */}
                <Box width="100%">
                  <Text textStyle="headingItem" marginBottom={2}>
                    Feedback
                  </Text>
                  <TextArea
                    gap={4}
                    id="justification"
                    placeholder="Add a feedback (optional)"
                    value={watch('justification')}
                    characterLimit={1000}
                    {...register('justification')}
                  />
                  {errors.justification && (
                    <FormFeedback variant="error">
                      {errors.justification.message}
                    </FormFeedback>
                  )}
                </Box>

                {/* @TODO: Show this banner only if reviewer requested access to score */}
                {receiveFeedback === 'yes' && (
                  <Banner
                    heading="For your attention"
                    variant="attention"
                    content="This Reviewer requested access to your score.
          They’ll receive an email with the details after you decide on this
            Submission."
                  />
                )}
                
                  <Box width="100%">
                    <Text textStyle="headingItem" marginBottom={2}>
                      Rate This Review
                    </Text>
                    <Checkbox
                      ariaLabel="WithEmail"
                      id="with-email"
                      name="withEmail"
                      onChange={e => setWithEmail(e.target.checked)}
                    >
                      with Email
                    </Checkbox>
                  </Box>
                
              </Flex>
            </GridItem>

            {withEmail && (
              <GridItem colSpan={12}>
                <Box mb={4}>{Divider}</Box>
                <FormProvider {...emailContentMethods}>
                  <ComposeEmail
                    emailTemplates={emailTemplates?.data}
                    emails={[email]}
                    reviewDetails={reviewDetails}
                  />
                </FormProvider>
              </GridItem>
            )}
          </Grid>
        </Layout>
        <Flex
          paddingY={4}
          paddingX={6}
          bottom={0}
          zIndex={99}
          justifyContent="center"
          position="sticky"
          bgColor="surfacePrimaryFill"
          marginX="auto"
        >
          <Flex justifyContent={'space-between'} gap={2} width="100%">
            <Button
              onClick={() => {
                resetForm();
                onClose();
              }}
              variant="caution"
            >
              Exit
            </Button>
            <ProtectedReviewWorkflowAction
              actionName="RateReview"
              reviewId={reviewId}
            >
              <Button variant="primary" type="submit">
                Submit Rating
              </Button>
            </ProtectedReviewWorkflowAction>
          </Flex>
        </Flex>
      </form>
    </Overlay>
  );
};

export default RateReviewWithEmail;
